import React from 'react';

import { styled } from '../theme';
import { Color, Size, matchSize } from '../utility/common-props-helpers';
import Text from './Text';

/**
 * Types
 */

type Props = {
    children: React.ReactNode;
    id?: string;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    size?: Size;
    color?: Color;
    onClick?: (ev: any) => void;
};

/**
 * Styled
 */

const BaseFab = styled(Text.withComponent('button'))<Props>(({ theme, disabled, color, size }) => ({
    height: matchSize({ medium: 30, large: 40 })(size),
    width: matchSize({ medium: 30, large: 40 })(size),
    fontSize: matchSize({ medium: '1.4rem', large: '1.6rem' })(size),
    cursor: 'pointer',
    borderRadius: '50%',
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
}));

const StyledFab = styled(BaseFab)<Props>(({ theme, disabled, color }) => ({
    color: theme.color.surfaceText,
    background: theme.color.surface,
    boxShadow: theme.boxShadow.surface,
    border: 'unset',
    ':hover': {
        color: theme.color.primaryText,
        background: theme.color.primary2,
    },
    ':active, &.active': {
        background: theme.color.primary3,
    },
    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        color: theme.color.disabled,
        background: theme.color.disabledText,
        boxShadow: 'unset',
    }),
}));

/**
 * Component
 */

export const Fab = React.forwardRef(
    ({ children, size = 'medium', color = 'base', type = 'button', ...props }: Props, ref) => {
        return (
            <StyledFab size={size} color={color} ref={ref} type={type} {...props}>
                {children}
            </StyledFab>
        );
    }
);

export default Fab;
